import React, { useState, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';import {
  Grid,
  Typography,
  TextField,
  Fab,
  Button,
  IconButton,
  Icon,
  Hidden
} from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress/LinearProgress";
import InputAdornment from "@material-ui/core/InputAdornment/InputAdornment";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { NotificationManager } from "react-notifications";
import { fontColor, parentContainer, primaryColor, webFont } from '../../config/theme';
import { client } from '../../config/apollo';
import FileUpload from "../../components/FileUpload";
import ImageView from "../../components/Image";
import { CREATE_POST, UPDATE_POST } from "../../graphql/mutations/post";
import Header from "../Header";
import Footer from "../Footer/FooterChefSteps";
import style from "../../containers/ChefSetup/style";
import Breadcrumbs from "../Breadcrumbs";

const PostSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required !")
    .min(3, "Minimum length 3 !"),
  body: Yup.string()
});

const AddPosts = props => {
  const dispatch = useDispatch();
  let postDraft = false;

  const { userId, isChef } = useSelector(store => ({
    userId: store.auth.userId,
    isChef: store.auth.isChef
  }));

  const [state, setState] = useReducer((oldProps, newProps) => ({ ...oldProps, ...newProps }), {
    title: "",
    tags: [],
    tag: "",
    postSlug: "",
    isDraft: false,
    loading: false,
    body: "",
    id: null,
    isError: false,
    media: [],
    postMedia: [],
    defaultTags: [],
    defaultSlug: null
  });

  const [errors, setErrors] = useReducer((oldProps, newProps) => ({ ...oldProps, ...newProps }), {
    title: null, body: null, postSlug: null,
  });

  // navigating back to home page if the user is not a chef
  useEffect(() => {
    if (!isChef) props.history.push('/home/dashboard');
  }, [isChef]);

  // calling APIs on load
  useEffect(() => {
    if (props.location) {
      if (props.location.state) {
        if (props.location.state.post) {
          const { tags, body, title, id, media, isDraft, postSlug } = props.location.state.post;

          setState({
            title,
            tags: tags,
            body,
            id,
            media,
            isDraft,
            postSlug,
          });
        }
      }
    }
  }, []);

  const addMedia = mediaData => {
    let {media} = state;
    if (mediaData && mediaData.url) {
      media.push(mediaData);
      setState({ media });
    }
  };

  const removeMedia = file => {
    const { media } = state;
    const removed = media.filter(f => {
      return file.url !== f.url;
    });
    setState({ media: removed });
  };

  const addPost = payload => {
    try {
      client.mutate({
        mutation: CREATE_POST,
        variables: payload
      })
        .then(PostRes => {
          setState({ loading: false, id: PostRes.data.createPost });
          if (postDraft) {
            NotificationManager.info('Post saved as draft', 'Add post', 3500)
            return;
          }
          props.history.push("/home/dashboard", { newPost: PostRes });
        })
        .catch(e => {
          let errorObj = JSON.parse(JSON.stringify(e));
          console.log(errorObj);
          setState({ loading: false });
          if (errorObj.graphQLErrors) {
            errorObj.graphQLErrors.map(error => {
              NotificationManager.error(error.message, "Add post");
            });
          }
        })
    } catch (err) {
      setState({ loading: false })
    }
  };

  const updatePost = payload => {
    try {
      client.mutate({
          mutation: UPDATE_POST,
          variables: payload
        })
        .then(PostRes => {
          setState({ loading: false, id: PostRes.data.updatePost });
          if (postDraft) {
            NotificationManager.info('Post saved as draft', 'Add post', 3500)
            return;
          }
          props.history.push("/home/dashboard", { newPost: PostRes });
        })
        .catch(e => {
          let errorObj = JSON.parse(JSON.stringify(e));
          console.log(errorObj);
          setState({ loading: false });
          if (errorObj.graphQLErrors) {
            errorObj.graphQLErrors.map(error => {
              NotificationManager.error(error.message, "Add post");
            });
          }
        })
    } catch (err) {
      setState({ loading: false })
    }
  };

  const handleSubmit = async values => {
    let { tags, isDraft, id, media } = state;
    let { title, body, postSlug } = values;
    const isUpdate = id && id.length > 0;

    if(title && title.trim().length<=0){
      setState({ isError: true });
      return;
    }

    if(postSlug && postSlug.trim().length<=0 && !postDraft){
      setState({ isError: true });
      NotificationManager.error('Post slug is mandatory', 'Add post');
      return;
    }

    if (body.length <= 0 && !postDraft) {
      setState({ isError: true });
      NotificationManager.error('Post description is mandatory', 'Add post');
      return;
    } else {
      setState({ isError: false });
    }

    if (media.length > 0) {
      media = media.map(item => {
        if (item.__typename) {
          delete item.__typename;
        }

        return item;
      });
    }

    let payload = {
      post: {
        chefId: userId,
        title,
        body: body,
        tags,
        isDraft: postDraft,
        media,
        postSlug: postSlug ? postSlug.toString().toLowerCase().replace(/ /g, '-') :
          title ? title.replace(/ /g, '-').toLowerCase() : '',
      }
    };

    if (isUpdate) {
      payload.post = { ...payload.post, id };
    }

    setState({ loading: true });

    try {
      const PostRes = isUpdate
        ? updatePost(payload)
        : addPost(payload);
    } catch (e) {
      let errorObj = JSON.parse(JSON.stringify(e));
      console.log(errorObj);
      setState({ loading: false });
      if (errorObj.graphQLErrors) {
        errorObj.graphQLErrors.map(error => {
          NotificationManager.error(error.message, "Add post");
        });
      }
    }
  };

  const handleUpdate = async values => {
    let { tags, isDraft, id, media } = state;
    let { title, body, postSlug } = state;
    const isUpdate = id && id.length > 0;
    isDraft = state.isDraft;

    if (title && title.trim().length<=0){
      setState({ isError: true });
      return;
    }

    if(postSlug && postSlug.trim().length<=0 && !postDraft){
      setState({ isError: true });
      NotificationManager.error('Post slug is mandatory', 'Add post');
      return;
    }

    if (body.length <= 0 && !postDraft) {
      setState({ isError: true });
      NotificationManager.error('Post description is mandatory', 'Add post');
      return;
    } else {
      setState({ isError: false });
    }

    if (media.length > 0) {
      media = media.map(item => {
        if (item.__typename) {
          delete item.__typename;
        }

        return item;
      });
    }

    let payload = {
      post: {
        chefId: userId,
        title,
        body: body,
        tags,
        isDraft: postDraft,
        media,
        postSlug: postSlug ? postSlug.toString().toLowerCase().replace(/ /g, '-') :
          state.postSlug ? state.postSlug :
            title ? title.replace(/ /g, '-').toLowerCase() : '',
      }
    };

    if (isUpdate) {
      payload.post = { ...payload.post, id };
    }

    setState({ loading: true });

    try {
      const PostRes = isUpdate
        ? updatePost(payload)
        : addPost(payload);
    } catch (e) {
      let errorObj = JSON.parse(JSON.stringify(e));
      console.log(errorObj);
      setState({ loading: false });
      if (errorObj.graphQLErrors) {
        errorObj.graphQLErrors.map(error => {
          NotificationManager.error(error.message, "Add post");
        });
      }
    }
  };

  const addTags = () => {
    const { tags, tag } = state;

    if(tag && tag.trim().length<=0)
      return;

    if (tags.find(c => c === tag)) {
      NotificationManager.error(tag + " is already selected", "Add Post");
      return;
    }
    tags.push(tag);
    setState({ tags, tag: "", isError: false });
  };

  return (
    <div style={parentContainer}>
      <Grid container>

        <Grid item xs={12}>
          <Header history={props.history} />
        </Grid>
        {state.loading && <LinearProgress color="primary" />}

        <Grid container className="add-post-container-custom" style={{marginTop: 40}}>

          <Grid item xs={12} style={{ marginBottom: 30 }}>
            <Breadcrumbs childRoutes={[
              {
                display: 'Posts',
                link: '#'
              },
              {
                display: state.id ? 'Edit Post' : 'Add Post',
                link: 'home/add-post'
              }
            ]} />
          </Grid>

          <Grid container>
            <Grid data-test-id={"createpost-id"} item xs={12}>
              <Typography variant="h5" style={styles.header}>
                {state.id ? 'Edit post' : 'Create post'}
              </Typography>
            </Grid>
          </Grid>

          <Form style={{ width: "100%", marginTop: 50, marginBottom: 30 }}>
            <Grid container>
              <Grid item direction="column" xs={12}>
                <TextField
                  name={"title"}
                  fullWidth={true}
                  placeholder={"Title *"}
                  type={"text"}
                  style={styles.textBox}
                  hideLabel={true}
                  onChange={evt => {
                    setState({ title: evt.target.value });
                    setErrors({ title: '' })
                  }}
                  onBlur={evt => {
                    if (evt.target.value) setErrors({ title: '' })
                    else setErrors({ title: 'Title is required!' +
                        '' })
                  }}
                  value={state.title}
                />
                <div className="post-err">{errors.title}</div>
              </Grid>
              <Grid item direction="column" xs={12} style={{ marginTop: 20 }}>
                <TextField
                  name={"postSlug"}
                  fullWidth={true}
                  placeholder={"Post slug for SEO *"}
                  type={"text"}
                  style={styles.textBox}
                  hideLabel={true}
                  onChange={evt => {
                    setState({ postSlug: evt.target.value });
                    setErrors({ postSlug: '' })
                  }}
                  onBlur={evt => {
                    if (evt.target.value) setErrors({ postSlug: '' })
                    else setErrors({ postSlug: 'Post Slug is required!' +
                        '' })
                  }}
                  value={state.postSlug = state.postSlug ?
                    state.postSlug.length < state.title.length ?
                      state.title.toString().toLowerCase().replace(/ /g, '-') :
                      state.postSlug.toString().toLowerCase().replace(/ /g, '-'): state.postSlug.toString().toLowerCase().replace(/ /g, '-') ?
                      state.postSlug : state.title ?
                        state.title.toString().toLowerCase().replace(/ /g, '-') : ''}
                />
                <div className="post-err">{errors.postSlug}</div>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              alignItems="center"
              justify="flex-start"
              style={{ marginTop: 30 }}
            >
              <Grid container>
                <Grid
                  item xs={12}>
                  <Typography
                    variant="h6"
                    style={styles.label}
                    gutterBottom={true}
                  >
                    Add banner image
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  // style={{ display: "flex" }}
                >
                  {!!state.media &&
                  !!state.media.length ?
                    state.media.map((image, i) => (
                      <ImageView
                        key={i + "image"}
                        url={image.url}
                        page="add-post"
                        onDelete={() => {
                          removeMedia(image);
                        }}
                        page={"post"}
                      />
                    )) : <FileUpload
                      onUploadStart={() => setState({ loading: true })}
                      onSuccess={res => {
                        const { data } = res;
                        addMedia(data);
                        setState({ loading: false });
                      }}
                      onError={err => {
                        console.log(err)
                        setState({ loading: false })
                      }}
                      fileType="IMAGE"
                      page={"post"}
                    />}
                </Grid>
              </Grid>
            </Grid>

            <Grid container style={{ marginTop: 30 }}>
              <Grid item xs={12}>
                <Grid container style={{ marginTop: 20, width: '100%', marginBottom: 30 }}>
                  <Typography
                    variant="h6"
                    style={styles.label}
                    gutterBottom={true}
                  >
                    Description *
                  </Typography>
                  <Grid item direction="column" xs={12}>
                    <TextField
                      fullWidth={true}
                      data-test-id={"postdesc"}
                      name={"body"}
                      placeholder={"Enter post description here"}
                      error={errors.body}
                      errorMessage={errors.body}
                      style={{ marginTop: 20 }}
                      type={"text"}
                      hideLabel={true}
                      multiline={true}
                      onChange={evt => {
                        setState({ body: evt.target.value });
                        setErrors({ body: '' })
                      }}
                      onBlur={evt => {
                        if (evt.target.value) setErrors({ body: '' })
                        else setErrors({ body: 'Post description is required!' +
                            '' })
                      }}
                      value={state.body}
                    />
                    <div className="post-err">{errors.body}</div>
                  </Grid>
                </Grid>

                <Grid container style={{ marginTop: 20 }}>
                  <Typography
                    variant="h6"
                    style={styles.label}
                    gutterBottom={true}
                  >
                    Tags
                  </Typography>
                  <Grid item md={12} xs={12}>
                    <TextField
                      inputProps={{ 'data-test-id': 'tag-input' }}
                      fullWidth={true}
                      type={"text"}
                      value={state.tag}
                      style={styles.textBox}
                      onChange={evt =>
                        setState({ tag: evt.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Add ingredients"
                              disabled={state.tag.length <= 0}
                              onClick={addTags}
                            >
                              <Icon
                                data-test-id={"addtagbtn"}
                                color={
                                  state.tag.length > 0
                                    ? "primary"
                                    : "action"
                                }
                              >
                                add_circle
                              </Icon>
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={8} style={{ marginTop: 50, paddingLeft: 30 }}>
                      {!state.id &&
                      state.defaultTags.map((item, index) => (
                        <Button
                          // fullWidth
                          key={index + item}
                          variant="outlined"
                          color="primary"
                          style={style.cuisineButton}
                          disableRipple={true}
                        >
                          {item}
                        </Button>
                      ))}
                      {state.tags.map((item, index) => (
                        <Button
                          // fullWidth
                          key={index + item}
                          variant="outlined"
                          color="primary"
                          style={style.cuisineButton}
                          disableRipple={true}
                          onClick={()=>{
                            const tags = state.tags;
                            tags.splice(index,1);
                            setState({ tags });
                          }}
                        >
                          {item}
                        </Button>
                      ))}
                    </Grid>
                  </Grid>

                </Grid>

                <Grid container style={{ marginTop: 20 }}>
                </Grid>
                <Grid
                  data-test-id={"postsub"}
                  container
                  alignItems="center"
                  justify="center"
                  direction="row"
                >
                  <Hidden smUp>
                    <Grid
                      data-test-id={"post-submit"}
                      item xs={10} style={{ textAlign: "center", marginTop: 50 }}>
                      {state.id && state.isDraft && <Fab
                        data-test-id={"postdraftbtn"}
                        variant={"extended"}
                        disabled={state.loading}
                        type={"submit"}
                        style={style.draftButton}
                        onClick={() => {
                          setState({
                            isDraft: true,
                          });
                          postDraft = true;
                          if (!state.id) { handleSubmit(state); }
                          else { handleUpdate(state); }
                        }}
                      >
                        Save as draft
                      </Fab>}
                      {!state.id && <Fab
                        data-test-id={"postdraftbtn"}
                        variant={"extended"}
                        disabled={state.loading}
                        type={"submit"}
                        style={style.draftButton}
                        onClick={() => {
                          setState({
                            isDraft: true,
                          });
                          postDraft = true;
                          if (!state.id) { handleSubmit(state); }
                          else { handleUpdate(state); }
                        }}
                      >
                        Save as draft
                      </Fab>}
                      <Fab
                        variant={"extended"}
                        type={"submit"}
                        disabled={state.loading}
                        // onClick={() => handleSubmit(values)}
                        style={
                          state.loading
                            ? style.submitButtonDisabled
                            : style.submitButton
                        }
                        onClick={() => {
                          setState({
                            isError: state.tags.length <= 0,
                            isDraft: false,
                          });
                          postDraft = false;
                          if (!state.id) { handleSubmit(state); }
                          else { handleUpdate(state); }
                        }}
                      >
                        {state.id ? "Update" : "Publish"}
                      </Fab>
                    </Grid>
                  </Hidden>
                  <Hidden xsDown>
                    <Grid item md={12} style={{ textAlign: "center", marginTop: 50 }}>
                      {state.id && state.isDraft && <Fab
                        data-test-id={"postdraftbtn"}
                        variant={"extended"}
                        disabled={state.loading}
                        type={"button"}
                        style={style.draftButton}
                        onClick={() => {
                          setState({
                            isDraft: true
                          });
                          postDraft = true;
                          if (!state.id) { handleSubmit(state); }
                          else { handleUpdate(state); }
                        }}
                      >
                        Save as draft
                      </Fab>}
                      {!state.id && <Fab
                        data-test-id={"postdraftbtn"}
                        variant={"extended"}
                        disabled={state.loading}
                        type={"button"}
                        style={style.draftButton}
                        onClick={() => {
                          setState({
                            isDraft: true
                          });
                          postDraft = true;
                          handleSubmit(state)
                        }}
                      >
                        Save as draft
                      </Fab>}
                      <Fab
                        data-test-id={"postsubmitbtn"}
                        variant={"extended"}
                        disabled={state.loading}
                        type={"button"}
                        style={
                          state.loading
                            ? style.submitButtonDisabled
                            : style.submitButton
                        }
                        onClick={() => {
                          setState({
                            isError: state.tags.length <= 0,
                          });
                          postDraft = false;
                          if (!state.id) { handleSubmit(state); }
                          else { handleUpdate(state); }
                        }}
                      >
                        {state.id && !state.isDraft ? "Update" : "Publish"}
                      </Fab>
                    </Grid>
                  </Hidden>
                </Grid>
              </Grid>
            </Grid>
          </Form>

        </Grid>

        <Footer />
      </Grid>
    </div>
  );
};

export default AddPosts;

const styles = {
  label: {
    fontFamily: webFont,
    fontSize: 24,
    fontWeight: 500
  },
  header: {
    fontFamily: webFont,
    fontSize: 32,
    fontWeight: 500
  },
  setFont: {
    fontFamily: webFont
  },
  heavy: {
    fontWeight: 800
  },
  littleHeavy: {
    fontWeight: 600
  },
  addMediaBox: {
    maxWidth: 154,
    height: 154,
    borderWidth: 1,
    borderColor: primaryColor,
    borderStyle: "solid",
    marginRight: 5
  },
  btnPlus: {
    marginTop: 55
  },
  btnPlusDishes: {
    marginLeft: 150
  },
  btnIcon: {
    color: fontColor
  },
  btnDishType: {
    borderRadius: "50%",
    borderColor: primaryColor,
    minWidth: 36,
    minHeight: 32,
    marginRight: 30,
    marginBottom: 10,
    borderWidth: 1,
    borderStyle: "solid",
    color: primaryColor,
    backgroundColor: "#fff"
  },
  btnDishTypeSelected: {
    borderRadius: "50%",
    borderColor: primaryColor,
    borderWidth: 1,
    borderStyle: "solid",
    color: fontColor,
    backgroundColor: primaryColor,
    minWidth: 36,
    minHeight: 32,
    marginRight: 30,
    marginBottom: 10
  },
  dishTypeDet: {
    fontSize: 10
  },
  textBox: {
    width: '100%',
    marginBottom: 30
  }
};
